import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslate } from "react-polyglot";
import { useBaseData } from "./BaseDataProvider.jsx";
import { numSumReducer, delay } from "../../Utils/misc.js";
import { useHttp } from "../Common/HttpProvider.jsx";
import useExtractRouteParamInt from "../../hooks/useExtractRouteParamInt.js";
import useEffectWithPreviousValues from "../../hooks/useEffectWithPreviousValues.js";
import useEffectAsync from "../../hooks/useEffectAsync.js";
import { useDialogs } from "../Common/DialogsProvider.jsx";
import { loadObjectFromStorage, saveObjectToStorage } from "../../Utils/StorageUtils.js";

/**
 * @typedef {Object} CourseProviderContext
 * @property {?Level} currentLevel
 * @property {?Branch} currentBranch
 * @property {?LevelsAndReward} levelsAndReward
 * @property {?Branch[]} branches
 * @property {?Branch[]} branchesForLevel
 * @property {?Deck} currentDeck
 * @property {Number} nextDeckId,
 * @property {String} exercisesStatistic
 * @property {String} wordsStatistic
 * @property {String} missionsStatistic
 * @property {String} branchesStatistic
 * @property {DataToProvideReview[]} eventsToFeedback
 * @property {ExercisesTreeForDeck[]} exercisesTreeForDeck
 * @property {?Object} scormProgress
 * @property {FnLevelVoid} setCurrentLevel
 * @property {FnLevelVoid} saveCurrentLevel
 * @property {FnLevelVoid} saveCurrentLevel
 * @property {FnBranchVoid} setCurrentBranch
 * @property {FnBranchVoid} saveCurrentBranch
 * @property {Function} updateCourseData
 * @property {FnEventsToFeedbackVoid} setEventsToFeedback
 * @property {FnAsyncPostFeedbackVoid} postUserReviews
 * @property {FnAsyncObjectBoolVoid} updateScormProgress
 */

/**
 * @type {React.Context<CourseProviderContext>}
 */
const courseContext = createContext({
  /** @type ?Level */
  currentLevel: null,
  /** @type ?Branch */
  currentBranch: null,
  /** @type ?LevelsAndReward */
  levelsAndReward: null,
  /** @type ?Branch[] */
  branches: null,
  /** @type ?Branch[] */
  branchesForLevel: null,
  /** @type ?Deck */
  currentDeck: null,
  nextDeckId: 0,
  exercisesStatistic: "0 / 0",
  wordsStatistic: "0 / 0",
  missionsStatistic: "0 / 0",
  branchesStatistic: "0 / 0",
  /** @type DataToProvideReview[] */
  eventsToFeedback: [],
  /** @type ExercisesTreeForDeck[] */
  exercisesTreeForDeck: [],
  /** @type ?Object */
  scormProgress: null,
  /** @type FnLevelVoid */
  setCurrentLevel: () => {},
  /** @type FnLevelVoid */
  saveCurrentLevel: () => {},
  /** @type FnLevelVoid */
  changeLevel: () => {},
  /** @type FnBranchVoid */
  setCurrentBranch: () => {},
  /** @type FnEventsToFeedbackVoid */
  setEventsToFeedback: () => {},
  /** @type FnBranchVoid */
  saveCurrentBranch: () => {},
  /** @type Function */
  updateCourseData: () => {},
  /** @type FnAsyncPostFeedbackVoid */
  postUserReviews: () => {},
  /** @type FnAsyncObjectBoolVoid */
  updateScormProgress: () => {},
  /** @type FnAsyncVoid */
  markDeckLearned: () => {},
});

/**
 * @returns {CourseProviderContext}
 */
export const useCourseProvider = () => useContext(courseContext);

/**
 * @param {React.ReactNode} children
 * @returns {React.ReactNode}
 * @constructor
 */
const CourseProvider = ({ children }) => {
  const { alert } = useDialogs();
  const snackbar = useDialogs();
  const t = useTranslate();
  const { get, post, patch } = useHttp();
  const history = useHistory();
  const { company, levelsAndReward, counts, getResources, branches, setBranches } = useBaseData();
  const [currentDeck, setCurrentDeck] = useState(/** @type ?Deck */ null);
  const [currentLevel, setCurrentLevel] = useState(/** @type ?Level */ loadObjectFromStorage("current_level") || null);
  const [currentBranch, setCurrentBranch] = useState(
    /** @type ?Branch */ loadObjectFromStorage("current_branch") || null,
  );
  const [branchesForLevel, setBranchesForLevel] = useState(/** @type ?Branch[] */ null);
  const [filteredDecks, setFilteredDecks] = useState(/** @type Deck[] */ []);
  const [nextDeckId, setNextDeckId] = useState(0);
  const [exercisesStatistic, setExercisesStatistic] = useState("0 / 0");
  const [wordsStatistic, setWordsStatistic] = useState("0 / 0");
  const [missionsStatistic, setMissionsStatistic] = useState("0 / 0");
  const [branchesStatistic, setBranchesStatistic] = useState("0 / 0");
  const [eventsToFeedback, setEventsToFeedback] = useState(/** @type DataToProvideReview[] */ []);
  const [exercisesTreeForDeck, setExercisesTreeForDeck] = useState(/** @type ExercisesTreeForDeck[] */ []);
  const [scormProgress, setScormProgress] = useState(/** @type ?Object */ null); // Значение null означает, что для
  // текущего deckId скорм-прогресс ещё не загрузился. Пустой объект означает, что прогресса по данному уроку ещё не
  // зарегистрировано.

  const deckId = useExtractRouteParamInt("/learn/:deckId", "deckId");

  const location = history.location.pathname;

  /**
   * Сохраняет выбранный раздел курса.
   *
   * @param {Branch} branch - Выбранный раздел.
   * @returns {void}
   */
  const saveCurrentBranch = useCallback((branch) => {
    saveObjectToStorage("current_branch", branch);
    setCurrentBranch(branch);
  }, []);

  /**
   * Сохраняет выбранный уровень курса.
   *
   * @param {Level} level - Выбранный уровень.
   * @returns {void}
   */
  const saveCurrentLevel = useCallback((level) => {
    saveObjectToStorage("current_level", level);
    setCurrentLevel(level);
  }, []);

  // clear states
  useEffect(() => {
    if (!company) {
      setBranches(null);
    }
  }, [company]);

  // events for feedback
  useEffectAsync(async () => {
    if (eventsToFeedback.length === 0) {
      /** @type * */
      const resp = await get("userReviews/getEventsToProvideReview");
      if (resp?.length > 0) {
        setEventsToFeedback(resp);
      }
    }
  }, []);

  // упражнения сгруппированы по тегам.
  useEffectAsync(async () => {
    if (currentDeck && currentDeck.exercises.items.length > 0) {
      let deckId = null;
      if (currentDeck.exam) {
        deckId = currentDeck.id;
      }
      await delay(600);

      const resp = await get(
        `exercises/tree/deck?exerciseIds=${JSON.stringify(currentDeck.exercises.items)}&deckId=${deckId}`,
      );
      if (currentDeck.exam) {
        setExercisesTreeForDeck(resp);
        return;
      }
      if (resp.length > 0) {
        // переставляем упражнения в последовательности, которая задана в уроке
        const newTree = resp.map((tag) => {
          const newExerciseSequence = [];
          currentDeck.exercises.items.forEach((idExercise) => {
            const itemInTags = tag.exercises.find((item) => item.id === idExercise);
            if (itemInTags) {
              newExerciseSequence.push(itemInTags);
            }
          });
          tag.exercises = newExerciseSequence;
          return tag;
        });
        setExercisesTreeForDeck(newTree);
      }
    } else {
      setExercisesTreeForDeck([]);
    }
  }, [currentDeck]);

  // update some data on location change
  useEffectWithPreviousValues(
    ([prevLocation]) => {
      // updateCourseData();
      // refreshUserInfo();
      // eslint-disable-next-line prettier/prettier
      if (prevLocation) {
        // ignore first render
        getResources(["branchesTree", "levels", "counts", "leaders", "userInfo"]).then();
      }
    },
    [location],
  );

  // request deck by deckId if it is specified
  useEffect(() => {
    // При смене урока сразу же надо обnullить прогресс. Это будет означать, что ожидается загрузка следующего урока
    setScormProgress(null);

    if (deckId) {
      requestDeckById(deckId).then();
    } else {
      clearCurrentDeck();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckId]);

  // set state by deckId specified in path
  useEffect(() => {
    if (!deckId || !branches || !levelsAndReward) {
      return;
    }
    branches.forEach((branch) => {
      branch.decks.forEach((deck) => {
        if (deck.id === deckId) {
          const currentLevel_ = levelsAndReward.levels.find((level) => branch.levelId === level.id);
          if (!currentLevel_) return;
          const filteredBranches = branches.filter((branch) => branch.levelId === currentLevel_.id);
          saveCurrentBranch(branch);
          saveCurrentLevel(currentLevel_);
          setBranchesForLevel(filteredBranches);
        }
      });
    });
  }, [deckId, branches, levelsAndReward, saveCurrentBranch, saveCurrentLevel]);

  //filter decks (open, not learned, count > 0)
  useEffect(() => {
    if (currentBranch && currentBranch.decks && currentBranch.decks.length > 0) {
      const newFilteredDecks = currentBranch.decks.filter(
        (deck) =>
          (currentDeck && deck.id === currentDeck.id) ||
          (deck.open &&
            ((deck.exercises.count > 0 && deck.exercises.count !== deck.exercises.learnedCount) ||
              (deck.words.count > 0 && deck.words.count !== deck.words.learnedCount) ||
              (deck.videos.count > 0 && deck.videos.count !== deck.videos.learnedCount))),
      );
      setFilteredDecks(newFilteredDecks);
    } else {
      setFilteredDecks([]);
    }
  }, [currentBranch, currentDeck]);

  //find nextDeckId
  useEffect(() => {
    if (currentDeck && filteredDecks.length > 0) {
      const deckIndex = filteredDecks.findIndex((el) => el.id === currentDeck.id);
      const nextDeckIndex = deckIndex + 1;

      if (nextDeckIndex && filteredDecks.length !== nextDeckIndex) {
        setNextDeckId(filteredDecks[nextDeckIndex].id);
      } else {
        setNextDeckId(0);
      }
    } else {
      setNextDeckId(0);
    }
  }, [currentDeck, filteredDecks]);

  // Сохранённый выбор должен соответствовать актуальному списку уровней, включая пустой список.
  useEffect(() => {
    const levels = levelsAndReward?.levels;
    if (!levels) return;

    const updatedLevel = levels.find((level) => level.id === currentLevel?.id);
    if (currentLevel && !updatedLevel) {
      setCurrentDeck(null);
      setScormProgress(null);
    }

    if (levels.length === 0) {
      localStorage.removeItem("current_level");
      localStorage.removeItem("current_branch");
      setCurrentLevel(null);
      setCurrentBranch(null);
      setBranchesForLevel([]);
      return;
    }

    const nextLevel = updatedLevel || ((!deckId || currentLevel) && levels[0]);
    if (nextLevel && nextLevel !== currentLevel) {
      saveCurrentLevel(nextLevel);
    }
  }, [levelsAndReward, currentLevel, deckId, saveCurrentLevel]);

  // Разделы могут обновляться отдельно от уровней, поэтому проверяем их согласованность перед выбором.
  useEffect(() => {
    if (!branches || !levelsAndReward?.levels) return;
    const levelExists = levelsAndReward?.levels.some((level) => level.id === currentLevel?.id);
    const filteredBranches = levelExists ? branches.filter((branch) => branch.levelId === currentLevel.id) : [];
    const branchIndex = currentBranch ? filteredBranches.findIndex((branch) => currentBranch.id === branch.id) : -1;
    setBranchesForLevel(filteredBranches);
    if (filteredBranches.length > 0) {
      saveCurrentBranch(filteredBranches[branchIndex === -1 ? 0 : branchIndex]);
    } else {
      localStorage.removeItem("current_branch");
      setCurrentBranch(null);
    }
  }, [branches, currentLevel, levelsAndReward, currentBranch, saveCurrentBranch]);

  // calculate statistics
  useEffect(() => {
    if (branches && branches.length > 0) {
      /** @type Deck[] */
      const decks = branches.flatMap((branch) => branch.decks);
      let exercisesLearnedCount = 0;
      let exercisesCount = 0;
      if (decks.length !== 0) {
        exercisesLearnedCount = decks.map((deck) => deck.exercises.learnedCount).reduce(numSumReducer);
        exercisesCount = decks.map((deck) => deck.exercises.count).reduce(numSumReducer);
      }
      const branchesCount = branches.length;
      const branchesLearned = branches.filter((branch) => branch.learned).length;
      setExercisesStatistic(`${exercisesLearnedCount} / ${exercisesCount}`);
      setBranchesStatistic(`${branchesLearned} / ${branchesCount}`);
    } else if (branches) {
      setExercisesStatistic("0 / 0");
      setBranchesStatistic("0 / 0");
    }
    if (counts) {
      const wordsLearned = counts.words.learnedCount + counts.phrases.learnedCount;
      const wordsTotal = counts.words.count + counts.phrases.count;
      setWordsStatistic(`${wordsLearned} / ${wordsTotal}`);
      setMissionsStatistic(`${counts.missions.learnedCount} / ${counts.missions.count}`);
    }
  }, [branches, counts]);

  // observe paid branch
  useEffectWithPreviousValues(
    /** @param {Branch} prevBranch */
    ([prevBranch]) => {
      if (!currentBranch) {
        return;
      }
      const isNewBranch = !prevBranch || prevBranch.id !== currentBranch.id;
      const shouldPrompt = isNewBranch && currentBranch.price !== 0 && !currentBranch.purchased;
      if (shouldPrompt) {
        showBuyPrompt(currentBranch);
      }
    },
    [currentBranch],
  );

  /** @param {Branch} branch */
  const showBuyPrompt = (branch) => {
    alert({
      title: branch.name,
      message: t("paidBranch"),
      confirmText: t("buyBranch", { price: branch.price, currency: branch.currency }),
    });
  };

  // /** @type FnAsyncVoid */
  // const requestBranches = async () => {
  //   const resp = await get("branchesTree");
  //   if (resp) {
  //     setBranches(resp);
  //   }
  // };

  /** @type FnAsyncNumberVoid */
  const requestDeckById = async (id) => {
    /** @type {Deck} */
    const resp = await get(`decks/${id}`);
    if (resp) {
      setCurrentDeck(resp);
      if (resp.isScorm) {
        const scormProgress_ = await get(`scorm/${id}`);
        setScormProgress(scormProgress_ || {});
      }
    }
  };

  /**
   * Обновить данные курса. Оборачиваем в useCallback, чтобы ссылка оставалась стабильной между рендерами
   */
  const updateCourseData = useCallback(() => {
    getResources(["branchesTree", "levels", "counts", "leaders"], true).then();

    // requestLevels().then();
    // requestBranches().then();
    // requestCounts().then();
    // requestLeaderboard().then();
  }, [getResources]);

  /** Сбросить текущий урок (deck) – useCallback для стабильности ссылки */
  const clearCurrentDeck = useCallback(() => {
    setCurrentDeck(null);
  }, []);

  /** Сменить уровень – useCallback для стабильности ссылки */
  /** @param {Level} level */
  const changeLevel = useCallback(
    (level) => {
      localStorage.removeItem("current_branch");
      saveCurrentLevel(level);
    },
    [saveCurrentLevel],
  );

  /** Отправить отзыв – useCallback для стабильности ссылки */
  /** @type FnAsyncPostFeedbackVoid */
  const postUserReviews = useCallback(
    async (postData) => {
      const resp = await post("userReviews", postData, (error) => {
        snackbar.error(error);
      });
      if (resp) snackbar.success(t("feedbackDialog.submitSuccess"));
    },
    [post, snackbar, t],
  );

  /** Обновить SCORM прогресс – useCallback для стабильности ссылки */
  /** @type FnAsyncObjectBoolVoid */
  const updateScormProgress = useCallback(
    async (scormData, silent = true) => {
      const resp = await post(`scorm/${deckId}`, scormData, null, null, silent);
      // после отправки прогресса скорм урока надо обновить прогресс в интерфейсе платформы
      await getResources(["branchesTree", "counts"], silent);
      // После того как прогресс станет видно пользователю, можно показать поздравление о том, что урок выучен, если он
      // действительно выучен
      if (resp.reward) {
        alert({
          title: t("Exercises.congratulation"),
          message: t("Exercises.congratText"),
          confirmText: "OK",
        });
      }
    },
    [post, deckId, getResources, alert, t],
  );

  /** Пометить урок как выученный – useCallback для стабильности ссылки */
  const markDeckLearned = useCallback(async () => {
    /** @type {{reward: Rewards}} */
    const resp = await patch(`decks/mark_learned/${deckId}`, {});
    if (!resp) return;

    await getResources(["branchesTree", "counts"], true);

    if (resp.reward.large) {
      alert({
        onConfirm: () => {
          switch (resp.reward.large.nextAction) {
            case "go_back":
            case "change_branch":
              history.push("/");
              break;
            case "change_deck":
              history.push(`/learn/${resp.reward.large.nextId}`);
              break;
            default:
              break;
          }
        },
        title: t("Exercises.congratulation"),
        message: resp.reward.large.items
          .map((item) => `${item.content}\n${item.congratulationText || ""}`)
          .join("\n\n"),
        confirmText: "OK",
      });
    }
  }, [patch, deckId, getResources, alert, history, t]);

  /**
   * Мемоизируем объект value контекста, чтобы потребители не ререндерились, когда данные реально не менялись
   */
  /** @type CourseProviderContext */
  const value = useMemo(
    () => ({
      currentLevel,
      currentBranch,
      levelsAndReward,
      branches,
      branchesForLevel,
      currentDeck,
      nextDeckId,
      exercisesStatistic,
      wordsStatistic,
      missionsStatistic,
      branchesStatistic,
      eventsToFeedback,
      exercisesTreeForDeck,
      scormProgress,
      setCurrentLevel,
      setCurrentBranch,
      updateCourseData,
      saveCurrentBranch,
      saveCurrentLevel,
      changeLevel,
      setEventsToFeedback,
      postUserReviews,
      updateScormProgress,
      markDeckLearned,
    }),
    [
      currentLevel,
      currentBranch,
      levelsAndReward,
      branches,
      branchesForLevel,
      currentDeck,
      nextDeckId,
      exercisesStatistic,
      wordsStatistic,
      missionsStatistic,
      branchesStatistic,
      eventsToFeedback,
      exercisesTreeForDeck,
      scormProgress,
      setCurrentLevel,
      setCurrentBranch,
      updateCourseData,
      saveCurrentBranch,
      saveCurrentLevel,
      changeLevel,
      setEventsToFeedback,
      postUserReviews,
      updateScormProgress,
      markDeckLearned,
    ],
  );
  return <courseContext.Provider value={value}>{children}</courseContext.Provider>;
};

export default CourseProvider;
